<template>
  <div id="app">
    <transition-group name="fade">

      <div v-show="startedQuiz === false" key="intro" class="intro">
        <div class="bg"></div>
        <div class="intro-inner">
          <h1>Predicting Shifts in Equilibrium: Q vs K</h1>
          <p>Calculate the value of the reaction quotient and compare it to the value of the equilibrium constant provided. Then predict which way the reaction will shift to reach equilibrium.</p>
          <button class="btn" v-on:click="startedQuiz = true">Begin Quiz</button>
        </div>
      </div>

      <quiz v-bind:questions="questions" v-on:restartQuiz="startedQuiz = false" v-show="startedQuiz === true" key="quizComponent"></quiz>

    </transition-group>
  </div>
</template>

<script>
import Quiz from './components/Quiz'

export default {
  name: 'app',
  components: {
    Quiz
  },
  data: function () {
    return {
      startedQuiz: false,
      questions: [
        {
          a: 'A',
          b: 'B<sub>2</sub>',
          ab: 'AB<sub>2</sub>',
          k: '6.8 &times; 10<sup>3</sup>',
          conc_a: '[0.0010]',
          conc_b: '[0.0020]',
          conc_ab: '[0.017]',
          answer: 8.5,
          exponent: 3,
          shift: 'left'
        },
        {
          a: 'A<sub>2</sub>',
          b: 'B<sub>2</sub>',
          ab: '2AB',
          k: '4.9 &times; 10<sup>2</sup>',
          conc_a: '[1.1]',
          conc_b: '[0.055]',
          conc_ab: '[0.95]',
          answer: 1.5,
          exponent: 1,
          shift: 'right'
        },
        {
          a: false,
          b: '2AB<sub>2</sub>',
          ab: 'A<sub>2</sub>B<sub>4</sub>',
          k: '3.2 &times; 10<sup>0</sup>',
          conc_a: false,
          conc_b: '[0.54]',
          conc_ab: '[0.93]',
          answer: 3.2,
          exponent: 0,
          shift: 'equal'
        },
        {
          a: '2A<sub>2</sub>B<sub>2</sub>',
          b: '2AB<sub>2</sub>',
          ab: 'B<sub>2</sub>',
          abflip: true,
          k: '9.1 &times; 10<sup>-1</sup>',
          conc_a: '[0.055]',
          conc_b: '[0.038]',
          conc_ab: '[0.46]',
          answer: 2.2,
          exponent: -1,
          shift: 'right'
        },
        {
          a: '2A',
          b: '3B<sub>2</sub>',
          ab: '2AB<sub>3</sub>',
          k: '5.6 &times; 10<sup>-3</sup>',
          conc_a: '[0.75]',
          conc_b: '[0.38]',
          conc_ab: '[0.013]',
          answer: 5.5,
          exponent: -3,
          shift: 'equal'
        },
        {
          a: 'A<sub>2</sub>',
          b: 'B<sub>2</sub>',
          ab: '2AB',
          k: '4.9 &times; 10<sup>2</sup>',
          conc_a: '[0.0031]',
          conc_b: '[0.026]',
          conc_ab: '[0.94]',
          answer: 1.1,
          exponent: 4,
          shift: 'left'
        },
        {
          a: '2A<sub>2</sub>B<sub>2</sub>',
          b: '2AB<sub>2</sub>',
          ab: 'B<sub>2</sub>',
          abflip: true,
          k: '9.1 &times; 10<sup>-1</sup>',
          conc_a: '[0.096]',
          conc_b: '[0.11]',
          conc_ab: '[0.69]',
          answer: 9.1,
          exponent: -1,
          shift: 'equal'
        },
        {
          a: '2A',
          b: '3B<sub>2</sub>',
          ab: '2AB<sub>3</sub>',
          k: '5.6 &times; 10<sup>-3</sup>',
          conc_a: '[0.88]',
          conc_b: '[0.75]',
          conc_ab: '[0.045]',
          answer: 6.2,
          exponent: -3,
          shift: 'left'
        },
        {
          a: 'A',
          b: 'B<sub>2</sub>',
          ab: 'AB<sub>2</sub>',
          k: '6.8 &times; 10<sup>3</sup>',
          conc_a: '[0.0020]',
          conc_b: '[0.014]',
          conc_ab: '[0.19]',
          answer: 6.8,
          exponent: 3,
          shift: 'equal'
        },
        {
          a: false,
          b: '2AB<sub>2</sub>',
          ab: 'A<sub>2</sub>B<sub>4</sub>',
          k: '3.2 &times; 10<sup>0</sup>',
          conc_a: false,
          conc_b: '[0.065]',
          conc_ab: '[0.68]',
          answer: 1.6,
          exponent: 2,
          shift: 'left'
        },
        {
          a: '2A',
          b: '3B<sub>2</sub>',
          ab: '2AB<sub>3</sub>',
          k: '5.6 &times; 10<sup>-3</sup>',
          conc_a: '[0.63]',
          conc_b: '[0.77]',
          conc_ab: '[0.0087]',
          answer: 4.2,
          exponent: -4,
          shift: 'right'
        },
        {
          a: 'A<sub>2</sub>',
          b: 'B<sub>2</sub>',
          ab: '2AB',
          k: '4.9 &times; 10<sup>2</sup>',
          conc_a: '[0.039]',
          conc_b: '[0.025]',
          conc_ab: '[0.69]',
          answer: 4.9,
          exponent: 2,
          shift: 'equal'
        },
        {
          a: false,
          b: '2AB<sub>2</sub>',
          ab: 'A<sub>2</sub>B<sub>4</sub>',
          k: '3.2 &times; 10<sup>0</sup>',
          conc_a: false,
          conc_b: '[0.96]',
          conc_ab: '[0.011]',
          answer: 1.2,
          exponent: -2,
          shift: 'right'
        },
        {
          a: '2A<sub>2</sub>B<sub>2</sub>',
          b: '2AB<sub>2</sub>',
          ab: 'B<sub>2</sub>',
          abflip: true,
          k: '9.1 &times; 10<sup>-1</sup>',
          conc_a: '[0.045]',
          conc_b: '[0.055]',
          conc_ab: '[0.66]',
          answer: 9.9,
          exponent: -1,
          shift: 'left'
        },
        {
          a: 'A',
          b: 'B<sub>2</sub>',
          ab: 'AB<sub>2</sub>',
          k: '6.8 &times; 10<sup>3</sup>',
          conc_a: '[0.0029]',
          conc_b: '[0.076]',
          conc_ab: '[0.0095]',
          answer: 4.3,
          exponent: 1,
          shift: 'right'
        }
      ]
    }
  }
}
</script>

<style lang="scss">
@import "assets/styles.scss";
.intro {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  h1 {
    font: bold 38px $font-family;
    color: $dk_grey;
    margin: 0 0 16px;
    letter-spacing: 2px;
  }
  p { 
    font-size: 27px; 
    letter-spacing: 1px;
    line-height: 36px;
  }
  p br { display: none; }
  .intro-inner { position: relative; }
  .bg {
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    background: #fff;
    opacity: 0.2;
  }
  .btn { margin-top: 20px; }
}
@media screen and (min-width: 600px) {
  .intro p br { display: block; }
}

.fade-enter-active, .fade-leave-active {
  transition-property: opacity;
  transition-duration: 0.25s;
}
.fade-enter-active {
  transition-delay: 0.35s;
}
.fade-enter, .fade-leave-active {
  opacity: 0;
}

.fadeQuiz-enter-active, .fadeQuiz-leave-active {
  transition: all 0.25s linear;
}
.fadeQuiz-enter {
  opacity: 0;
}
.fadeQuiz-leave-active {
  opacity: 0;
  transform: translateX(70%);
}

.btn {
  border: none;
  background: $blue;
  color: #fff;
  padding: 12px 50px;
  text-transform: uppercase;
  font: bold 20px $font-family;
  letter-spacing: 3px;
  cursor: pointer;
}
p { 
  color: $dk_grey;
  font-size: 20px;
  letter-spacing: 1.5px;
  margin: 0 0 20px;
}
</style>


// WEBPACK FOOTER //
// App.vue?70fc2098